import React from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { Button } from '@/components/ui/button';
import { normalizeBuilderLink, isBlankHtml, navigateBuilderCta } from '@/utils/functions';
import { htmlSeguro } from '../../utils/htmlSeguro';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
import {
  EstilosFondoMovil,
  FondoDelHero,
  PropsFondoMovil,
  atributosCajaFondoMovil,
  usaFondoMovil,
} from '../../utils/fondoResponsive';

interface HeroWithBackgroundProps extends PropsFondoMovil {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonTextSecondary?: string;
  buttonLinkSecondary?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonSecondaryBgColor?: string;
  buttonSecondaryTextColor?: string;
  backgroundImage?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  textColor?: string;
  textAlignment?: 'left' | 'center' | 'right';
  height?: string;
  children?: React.ReactNode;
}

export const HeroWithBackground = ({
  title = 'Encuentra tu próximo auto',
  subtitle = 'Amplio inventario de autos seminuevos verificados y con garantía',
  buttonText = 'Ver vehículos',
  buttonLink = '/vehicles',
  buttonTextSecondary = 'Contactar',
  buttonLinkSecondary = '/contact',
  buttonBgColor = '#3b82f6',
  buttonTextColor = '#ffffff',
  buttonSecondaryBgColor = 'transparent',
  buttonSecondaryTextColor = '#ffffff',
  backgroundImage = 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=1470',
  overlayColor = '#000000',
  overlayOpacity = 0.5,
  textColor = '#ffffff',
  textAlignment = 'center',
  height = '500px',
  mobileImage,
  mobileFit,
  mobilePosition,
  mobileHeight,
  mobileBackdropColor,
  children,
}: HeroWithBackgroundProps) => {
  const { connectors, selected } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.options.enabled,
  }));

  const overlayStyle = {
    backgroundColor: overlayColor,
    opacity: overlayOpacity,
  };

  const propsMovil: PropsFondoMovil = {
    mobileImage, mobileFit, mobilePosition, mobileHeight, mobileBackdropColor,
  };
  const { style: varsMovil, ...atributosMovil } = atributosCajaFondoMovil(propsMovil) as {
    style?: React.CSSProperties; [k: string]: unknown;
  };

  // Respeta el link del builder: navega si es ruta/URL, scroll si es #ancla.
  const scrollToVehicles = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isEnabled) return;
    navigateBuilderCta(buttonLink);
  };

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      {...(atributosMovil as Record<string, string>)}
      style={{
        height,
        position: 'relative',
        color: textColor,
        border: selected ? '1px dashed #1e88e5' : 'none',
        ...varsMovil,
      }}
      className='w-full flex items-center'
    >
      {usaFondoMovil(propsMovil) && <EstilosFondoMovil />}
      {/* Fondo: antes iba como `background-image` de esta misma caja. Ahora es
          un elemento propio —se pinta idéntico— para que en móvil pueda cambiar
          de archivo y salir del posicionamiento absoluto. */}
      <FondoDelHero imagen={backgroundImage} mobileImage={mobileImage} prioridad />

      {/* Overlay */}
      <div style={overlayStyle} className='absolute inset-0 z-0' />

      {/* Content */}
      <AreaDePiezas className='container mx-auto px-4 z-10 relative'>
        <div
          className={`max-w-3xl mx-auto text-${textAlignment}`}
          style={{ margin: textAlignment === 'center' ? '0 auto' : '0' }}
        >
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            {!isBlankHtml(title) && (
              <h1
                style={{ color: textColor }}
                className='text-4xl md:text-5xl font-bold mb-4'
                dangerouslySetInnerHTML={htmlSeguro(title || '')}
              />
            )}
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            {!isBlankHtml(subtitle) && (
              <p style={{ color: textColor }} className='text-lg md:text-xl mb-8'
                dangerouslySetInnerHTML={htmlSeguro(subtitle || '')}
              />
            )}
          </Pieza>
          <Pieza clave="botones" etiqueta="Botones">
            {(!isBlankHtml(buttonText) || !isBlankHtml(buttonTextSecondary)) && (
              <div className='flex flex-wrap gap-4 justify-center'>
                {!isBlankHtml(buttonText) && (
                  <Button
                    className='px-8 py-3 rounded-md transition-colors'
                    style={{
                      backgroundColor: buttonBgColor,
                      color: buttonTextColor,
                    }}
                    onClick={scrollToVehicles}
                  >
                    <span dangerouslySetInnerHTML={htmlSeguro(buttonText || '')} />
                  </Button>
                )}
                {!isBlankHtml(buttonTextSecondary) && (
                  <Button
                    asChild
                    variant='outline'
                    className='px-8 py-3 rounded-md border-2 transition-colors'
                    style={{
                      backgroundColor: buttonSecondaryBgColor,
                      color: buttonSecondaryTextColor,
                      borderColor: buttonSecondaryTextColor,
                    }}
                  >
                    <a href={normalizeBuilderLink(buttonLinkSecondary)} dangerouslySetInnerHTML={htmlSeguro(buttonTextSecondary || '')} />
                  </Button>
                )}
              </div>
            )}
          </Pieza>
          {children}
        </div>
      </AreaDePiezas>
    </div>
  );
};

const HeroWithBackgroundSettings = () => {
  const { actions, selected } = useEditor((state) => {
    const currentNodeId = state.events.selected;
    let selectedNode = null;

    if (currentNodeId) {
      const nodeId = Array.from(currentNodeId as Set<string>)[0];
      if (nodeId && state.nodes[nodeId]) {
        selectedNode = {
          id: nodeId,
          data: state.nodes[nodeId].data,
          props: state.nodes[nodeId].data.props,
        };
      }
    }

    return {
      selected: selectedNode,
    };
  });

  if (!selected) return null;

  return (
    <div className='space-y-4'>
      <div>
        <label className='text-sm font-medium mb-1 block'>Título</label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.title || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.title = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>Subtítulo</label>
        <textarea
          className='w-full p-2 border rounded text-sm'
          rows={3}
          value={selected.props.subtitle || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.subtitle = e.target.value;
            });
          }}
        />
      </div>

      {/* Primer botón */}
      <div className='pt-2 border-t'>
        <label className='text-sm font-medium mb-1 block'>
          Texto del botón principal
        </label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.buttonText || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.buttonText = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Enlace del botón principal
        </label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.buttonLink || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.buttonLink = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color de fondo del botón principal
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.buttonBgColor || '#3b82f6'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonBgColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.buttonBgColor || '#3b82f6'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonBgColor = e.target.value;
              });
            }}
          />
        </div>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color de texto del botón principal
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.buttonTextColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonTextColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.buttonTextColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonTextColor = e.target.value;
              });
            }}
          />
        </div>
      </div>

      {/* Segundo botón */}
      <div className='pt-2 border-t'>
        <label className='text-sm font-medium mb-1 block'>
          Texto del botón secundario
        </label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.buttonTextSecondary || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.buttonTextSecondary = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Enlace del botón secundario
        </label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.buttonLinkSecondary || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.buttonLinkSecondary = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color de fondo del botón secundario
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.buttonSecondaryBgColor || 'transparent'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonSecondaryBgColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.buttonSecondaryBgColor || 'transparent'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonSecondaryBgColor = e.target.value;
              });
            }}
          />
        </div>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color de texto del botón secundario
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.buttonSecondaryTextColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonSecondaryTextColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.buttonSecondaryTextColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.buttonSecondaryTextColor = e.target.value;
              });
            }}
          />
        </div>
      </div>

      {/* Fondo y otros ajustes */}
      <div className='pt-2 border-t'>
        <label className='text-sm font-medium mb-1 block'>
          URL de imagen de fondo
        </label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.backgroundImage || ''}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.backgroundImage = e.target.value;
            });
          }}
        />
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color de superposición
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.overlayColor || '#000000'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.overlayColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.overlayColor || '#000000'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.overlayColor = e.target.value;
              });
            }}
          />
        </div>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Opacidad de superposición
        </label>
        <div className='flex items-center space-x-2'>
          <input
            type='range'
            min='0'
            max='1'
            step='0.1'
            className='flex-1'
            value={selected.props.overlayOpacity || 0.5}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.overlayOpacity = parseFloat(e.target.value);
              });
            }}
          />
          <span className='w-10 text-center'>
            {(selected.props.overlayOpacity || 0.5) * 100}%
          </span>
        </div>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Color del texto
        </label>
        <div className='flex items-center'>
          <input
            type='color'
            className='w-10 h-10 p-1 border rounded'
            value={selected.props.textColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.textColor = e.target.value;
              });
            }}
          />
          <input
            type='text'
            className='flex-1 p-2 border rounded text-sm ml-2'
            value={selected.props.textColor || '#ffffff'}
            onChange={(e) => {
              actions.setProp(selected.id, (props: any) => {
                props.textColor = e.target.value;
              });
            }}
          />
        </div>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>
          Alineación del texto
        </label>
        <select
          className='w-full p-2 border rounded text-sm'
          value={selected.props.textAlignment || 'center'}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.textAlignment = e.target.value;
            });
          }}
        >
          <option value='left'>Izquierda</option>
          <option value='center'>Centro</option>
          <option value='right'>Derecha</option>
        </select>
      </div>
      <div>
        <label className='text-sm font-medium mb-1 block'>Altura</label>
        <input
          type='text'
          className='w-full p-2 border rounded text-sm'
          value={selected.props.height || '500px'}
          onChange={(e) => {
            actions.setProp(selected.id, (props: any) => {
              props.height = e.target.value;
            });
          }}
        />
      </div>
    </div>
  );
};

HeroWithBackground.craft = {
  displayName: 'HeroWithBackground',
  props: {
    title: 'Encuentra tu próximo auto',
    subtitle:
      'Amplio inventario de autos seminuevos verificados y con garantía',
    buttonText: 'Ver vehículos',
    buttonLink: '/vehicles',
    buttonTextSecondary: 'Contactar',
    buttonLinkSecondary: '/contact',
    buttonBgColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    buttonSecondaryBgColor: 'transparent',
    buttonSecondaryTextColor: '#ffffff',
    backgroundImage:
      'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=1470',
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    textColor: '#ffffff',
    textAlignment: 'center',
    height: '500px',
  },
  related: {
    settings: HeroWithBackgroundSettings,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
  isCanvas: true,
};

export { HeroWithBackgroundSettings };
